import { useState } from 'react';
import { T } from 'gt-react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import useGetInboundEmail from '@/api/core/inbound-emails/getInboundEmail';
import { SmartEmailViewer } from './SmartEmailViewer';
import { inboundEmailToEmailData } from './utils';

interface Props {
  inboundEmailId: string;
}

/**
 * Fetches a single inbound email by id and renders it through the canonical
 * SmartEmailViewer — thread view when the email carries a conversationId,
 * single-email view otherwise. Shared by the collection-thread emails section
 * and the docified-email-body document path.
 */
export function InboundEmailViewer({ inboundEmailId }: Props) {
  const [showSplitDocuments, setShowSplitDocuments] = useState(false);
  const {
    data: email,
    isLoading,
    isFetching,
    error,
  } = useGetInboundEmail({
    inboundEmailId,
    includeSplitDocuments: showSplitDocuments,
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !email) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <T context="error state, inbound email could not be loaded">
          Email not found
        </T>
      </div>
    );
  }

  return (
    <SmartEmailViewer
      emailData={inboundEmailToEmailData(email)}
      conversationId={email.conversationId}
      defaultTab="email"
      sourceEmailId={email.id}
      highlightedEmailIds={[email.id]}
      splitDocuments={{
        show: showSplitDocuments,
        onToggle: () => setShowSplitDocuments((prev) => !prev),
        isLoading: isFetching,
      }}
    />
  );
}
